import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Play } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, Button } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataTableAction, type DataTableColumn } from '@/components/ui/DataTable';
import { standardReportsService, saveBlob } from '@/services/standardReports.service';
import { customReportsApi } from '@/services/customReports.service';
import { getErrorMessage } from '@/services/apiClient';
import { useAuth } from '@/hooks/useAuth';
import { PERMISSION } from '@/lib/permissions';
import type { ReportResult } from '@/types/customReports.types';
import type { StandardReportRun } from '@/types/standardReports.types';
import type { BadgeVariant } from '@/types/common.types';
import { formatFilterTextOneLine, resultIdOf, resultTypeOf } from './resultHelpers';

/**
 * One report, and every run of it.
 *
 * THIS IS WHAT REPLACED THE HISTORY PAGE. History listed every run of every
 * report in one flat table with a tab strip for the backend that produced it,
 * which meant finding last month's close involved scanning past every other
 * report's runs. Runs only ever matter in the context of the report they belong
 * to, so they live under it.
 *
 * WHY THE SOURCES ARE NOT UNIFIED HERE. Each kind stores its runs differently
 * and only the standard kind records a period, a row count and a total per run.
 * Rather than flatten all three to the poorest common shape, each renders what
 * it actually has — a custom report's runs are its generated results, an AI
 * report's single run is the answer itself.
 */
export default function ReportDetailPage() {
  const { kind = '', id = '' } = useParams();
  const navigate = useNavigate();
  const numericId = Number(id);
  // This page is reachable without permission 15; the runner it offers to send
  // people back to is not. Offering the button anyway is a button that bounces.
  const { hasPermission } = useAuth();
  const canRun = hasPermission(PERMISSION.REPORTS);

  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  // Filtered by the SERVER on orderId. The first version fetched every run and
  // filtered client-side with `runId === numericId`, comparing an order id to a
  // run id — which matches nothing, so this page was always empty.
  const standardRuns = useQuery({
    queryKey: ['reports', 'standard-runs', 'order', numericId],
    queryFn: () => standardReportsService.listRuns({ orderId: numericId, limit: 200 }),
    enabled: kind === 'standard' && Number.isFinite(numericId) && numericId > 0,
    staleTime: 30_000,
    retry: false,
  });

  const customResults = useQuery({
    queryKey: ['reports', 'results', 'cst', { orderId: numericId }],
    queryFn: () => customReportsApi.results.list({ type: 'cst', orderId: numericId }),
    enabled: kind === 'custom',
    staleTime: 30_000,
    retry: false,
  });

  const downloadStandard = async (run: StandardReportRun) => {
    setDownloadingId(run.runId);
    setDownloadError(null);

    try {
      const blob = await standardReportsService.downloadRun(run.runId);
      saveBlob(blob, `${run.reportKey}_${run.runId}.xlsx`);
    } catch (e) {
      setDownloadError(getErrorMessage(e, 'Could not download the workbook.'));
    } finally {
      setDownloadingId(null);
    }
  };

  const standardColumns: DataTableColumn<StandardReportRun>[] = [
    {
      key: 'windowStart',
      header: 'Period',
      render: (run) =>
        run.windowStart && run.windowEnd ? (
          <span className="text-slate-700">
            {run.windowStart.slice(0, 10)} → {run.windowEnd.slice(0, 10)}
          </span>
        ) : (
          // After the period fix this means a report deliberately run over all
          // time, not a resolution that silently failed.
          <span className="text-slate-400">All dates</span>
        ),
    },
    {
      key: 'rowCount',
      header: 'Rows',
      width: 'w-[12%]',
      align: 'right',
      render: (run) => (run.rowCount === null ? '—' : run.rowCount.toLocaleString()),
    },
    {
      key: 'grandTotal',
      header: 'Total',
      width: 'w-[14%]',
      align: 'right',
      // Exact decimal string from the server — rendered, never parsed.
      render: (run) => run.grandTotal ?? '—',
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-[14%]',
      render: (run) => <Badge variant={statusBadge(run.status)}>{run.status}</Badge>,
    },
    {
      key: 'completedAt',
      header: 'Generated',
      width: 'w-[18%]',
      render: (run) => (run.completedAt ? new Date(run.completedAt).toLocaleString() : '—'),
    },
  ];

  const standardActions: DataTableAction<StandardReportRun>[] = [
    {
      label: 'View',
      // The RUN, not the runner. This pointed at `/reports/standard/{key}?run=`
      // and the runner reads only `reportKey` from the route — so opening a
      // finished run showed an empty form asking you to run it again.
      onClick: (run) => navigate(`/reports/standard-runs/${run.runId}`),
    },
    {
      label: 'Download workbook',
      onClick: (run) => void downloadStandard(run),
      hidden: (run) => !run.hasFile,
      disabled: (run) => downloadingId === run.runId,
    },
  ];

  const runsForOrder = standardRuns.data ?? [];

  // A custom report's runs ARE its results, and the viewer that renders one
  // already exists at /reports/results/:id. Without this the list was a dead
  // end: rows displayed, nothing opened them, and the report was unreachable.
  // `type` is read off the row rather than assumed — a cdm row reaching this
  // list would otherwise be looked up in the cst table and 404.
  const openResult = (row: ReportResult) =>
    navigate(`/reports/results/${resultIdOf(row)}?type=${resultTypeOf(row)}`);

  // Taken from the runs themselves rather than the URL: the route carries the
  // ORDER id, and only a run knows which report key it belongs to.
  const reportKey = runsForOrder[0]?.reportKey ?? '';

  return (
    <div className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto">
      <div className="shrink-0 p-6">
        <button
          type="button"
          onClick={() => navigate('/reports/my-reports')}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4" />
          My Reports
        </button>

        <PageHeader
          variant="compact"
          title="Report runs"
          actions={
            // Back to THIS report's runner, not the catalog. Sending the user to
            // /reports made them find the report again by hand.
            kind === 'standard' && reportKey && canRun ? (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Play className="h-4 w-4" />}
                onClick={() => navigate(`/reports/standard/${reportKey}`)}
              >
                Run it again
              </Button>
            ) : null
          }
        />

        {downloadError && <Alert variant="error">{downloadError}</Alert>}

        {kind === 'ai' && (
          <Alert variant="info">
            AI reports produce a single answer rather than a series of runs.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => navigate(`/reports/ai-results/${numericId}`)}
            >
              Open it
            </button>
            .
          </Alert>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        {kind === 'standard' && (
          <DataTable<StandardReportRun>
            data={runsForOrder}
            columns={standardColumns}
            keyField="runId"
            rowActions={standardActions}
            onRowClick={(run) => navigate(`/reports/standard-runs/${run.runId}`)}
            loading={standardRuns.isLoading}
            tableFixed
            className="[&>div]:overflow-x-hidden"
            emptyTitle="No runs yet"
            emptyDescription="This report has not been run."
          />
        )}

        {kind === 'custom' && (
          <DataTable<ReportResult>
            data={customResults.data ?? []}
            columns={[
              { key: 'title', header: 'Name', truncate: true },
              {
                key: 'reportTitle',
                header: 'Report',
                width: 'w-[22%]',
                truncate: true,
                render: (r) => r.reportTitle || r.selectedReport || '—',
              },
              {
                key: 'filterText',
                header: 'Filters',
                width: 'w-[32%]',
                truncate: true,
                // `filterText` is stored URL-encoded in the legacy segment
                // format; rendering the column raw prints that encoding at the
                // user. The parser for it already exists.
                render: (r) => formatFilterTextOneLine(r.filterText) || '—',
              },
              { key: 'dateCreated', header: 'Generated', width: 'w-[18%]', truncate: true },
            ]}
            keyField="reportsCustomResultsID"
            rowActions={[{ label: 'Open', onClick: openResult }]}
            onRowClick={openResult}
            loading={customResults.isLoading}
            tableFixed
            className="[&>div]:overflow-x-hidden"
            emptyTitle="No runs yet"
            emptyDescription="This report has not been generated."
          />
        )}
      </div>
    </div>
  );
}

function statusBadge(status: string): BadgeVariant {
  if (status === 'ready') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'too_large' || status === 'empty') return 'warning';

  return 'neutral';
}
